import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import styles from "./Field.module.css";
import { cx } from "./cx.js";

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Given the wiring a control needs to be described by its label, hint and error. */
  children: (props: {
    id: string;
    "aria-describedby": string | undefined;
    "aria-invalid": true | undefined;
    required: boolean | undefined;
  }) => ReactNode;
}

/**
 * Composing label, hint and error in one place means accessible form markup
 * happens by default rather than by remembering `aria-describedby`.
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = cx(hint && hintId, error && errorId) || undefined;

  return (
    <div className={styles.field}>
      <label htmlFor={id} className={cx(styles.label, required && styles.required)}>
        {label}
      </label>
      {children({
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
        required: required || undefined,
      })}
      {hint && !error && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
      {/* Errors are announced, because a form that fails silently on a phone
          scrolls the message out of view. */}
      {error && (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  numeric?: boolean;
}

export function Input({ invalid, numeric, className, ...rest }: InputProps) {
  return (
    <input
      className={cx(
        styles.input,
        numeric && styles.numeric,
        (invalid || rest["aria-invalid"]) && styles.invalid,
        className,
      )}
      {...rest}
    />
  );
}
